<template>
  <v-dialog v-model="dialog" max-width="1400px" >
    <v-card class="fontNotoSans400">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ year }}년 임직원 보수 일람표</span>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="downloadExcel" :loading="loading">
          <v-icon left>mdi-download</v-icon>
          엑셀 다운로드
        </v-btn>
        <v-btn icon="mdi-close" variant="text" @click="closeModal"></v-btn>
      </v-card-actions>
    </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="filteredSalaryData"
          :items-per-page="-1"
          class="elevation-1"
          :loading="loading"
        >
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.pay="{ item }">
            <div class="text-right">{{ formatNumber(item.pay || 0) }}원</div>
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.out_pay="{ item }">
            <div class="text-right">{{ formatNumber(item.out_pay || 0) }}원</div>
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.social_pay="{ item }">
            <div class="text-right">{{ formatNumber(item.social_pay || 0) }}원</div>
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.total_pay="{ item }">
            <div class="text-right"><strong>{{ formatNumber(item.total_pay || 0) }}원</strong></div>
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.expense_type="{ item }">
            <v-chip 
              :color="item.expense_type === '직접비' ? 'green' : 'blue'" 
              size="small"
            >
              {{ item.expense_type }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="downloadExcel" :loading="loading">
          <v-icon left>mdi-download</v-icon>
          엑셀 다운로드
        </v-btn>
        <v-btn color="grey" @click="closeModal">닫기</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

// 세금/보험 요율 인터페이스
interface TaxData {
  tr_idx?: number;
  tr_year?: string;
  tr_time_pay?: number;
  tr_yeon?: number;
  tr_gun?: number;
  tr_go?: number;
  tr_jang?: number;
  tr_sil?: number;
  tr_san?: number;
  tr_labor_cost_rate?: number;
}

// 예산 데이터 인터페이스
interface BudgetData {
  fc_scode: string;
  fc_sname?: string;
  description?: string;
  unit_price?: string;
  count?: string;
  months?: string;
  hb_pay_rel?: string;
  isSalaryItem?: boolean;
  isLinkedItem?: boolean;
}

// 가공된 급여 데이터 인터페이스
interface SalaryItem {
  name: string;
  job: string;
  w4c_pay_gubun: string;
  expense_type: string;
  biz_gubun: string;
  pay: number;
  out_pay: number;
  social_pay: number;
  total_pay: number;
}

// Props
interface Props {
  modelValue: boolean;
  year: string;
  budgetData: BudgetData[];
  taxData?: TaxData;
  bizGubun?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  showSnackbar: [message: string, type: string];
}>();

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const loading = ref(false);

// 사업구분 선택 관련
const selectedBizGubun = ref('전체');

// 사업 유형에 따른 급여유형 이름
const getGubunName = (bizGubun: string | undefined) => {
  switch (bizGubun) {
    case 'juga': return '재가장기요양기관 주야간보호';
    case 'danggi': return '재가장기요양기관 단기보호';
    case 'yoyang': return '재가장기요양기관 방문요양';
    case 'mokuk': return '재가장기요양기관 방문목욕';
    case 'ganho': return '재가장기요양기관 방문간호';
    case 'bokji': return '재가장기요양기관 복지용구';
    default: return '재가장기요양기관 방문요양';
  }
};

// 숫자 파싱 (콤마 제거)
const parseNumber = (value: string | number | undefined): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  return Number(value.toString().replace(/,/g, '')) || 0;
};

// 급여 항목인지 확인
const isSalaryCode = (scode: string): boolean => {
  return ['1111', '1112'].includes(scode);
};

// 퇴직금 항목인지 확인
const isRetirementCode = (scode: string): boolean => {
  return ['1141', '1142'].includes(scode);
};

// 사회보험부담금 항목인지 확인
const isInsuranceCode = (scode: string): boolean => {
  return ['1151', '1152'].includes(scode);
};

// 인건비 구분 추출 (scode 기반)
const getLaborCostType = (scode: string): string => {
  if (['1111', '1141', '1151'].includes(scode)) {
    return '직접비';
  }
  if (['1112', '1142', '1152'].includes(scode)) {
    return '간접비';
  }
  return '';
};

// 직종 추출 (description에서) - 대표자도 일반 직원과 동일하게 표시
const extractJobType = (description: string): string => {
  if (description.includes('센터장') || description.includes('시설장')) {
    return '시설장(관리책임자)';
  }
  if (description.includes('사회복지사')) {
    return '사회복지사';
  }
  if (description.includes('요양보호사')) {
    return '요양보호사 1급';
  }
  return description.replace(' 급여', '').replace(' 퇴직금', '').replace(' 사회보험부담금', '');
};

// budgetData에서 salaryData 생성
const salaryData = computed<SalaryItem[]>(() => {
  const items: SalaryItem[] = [];
  
  // 급여 항목만 추출
  const salaryItems = props.budgetData.filter(item => 
    isSalaryCode(item.fc_scode) && item.description
  );

  // 퇴직금, 사회보험 항목 추출
  const retirementItems = props.budgetData.filter(item => 
    isRetirementCode(item.fc_scode) && item.description
  );
  const insuranceItems = props.budgetData.filter(item => 
    isInsuranceCode(item.fc_scode) && item.description
  );

  const salaryTypeName = getGubunName(props.bizGubun);

  // 각 급여 항목에 대해 처리
  salaryItems.forEach(salaryItem => {
    const count = parseNumber(salaryItem.count) || 1;
    const months = parseNumber(salaryItem.months) || 12;
    const unitPrice = parseNumber(salaryItem.unit_price);
    const annualSalary = unitPrice * months;
    
    const jobType = extractJobType(salaryItem.description || '');
    const laborCostType = getLaborCostType(salaryItem.fc_scode);
    const payRelGroup = salaryItem.hb_pay_rel;

    // 연결된 퇴직금/사회보험 찾기
    let retirementAmount = 0;
    let insuranceAmount = 0;

    if (payRelGroup) {
      const linkedRetirement = retirementItems.find(r => r.hb_pay_rel === payRelGroup);
      const linkedInsurance = insuranceItems.find(i => i.hb_pay_rel === payRelGroup);

      if (linkedRetirement) {
        const retUnitPrice = parseNumber(linkedRetirement.unit_price);
        // 금액 = 단가 * 개월, 천 원 단위 반올림 (1의 자리 반올림)
        retirementAmount = Math.round(retUnitPrice * months / 1000) * 1000;
      }
      if (linkedInsurance) {
        const insUnitPrice = parseNumber(linkedInsurance.unit_price);
        // 금액 = 단가 * 개월, 천 원 단위 반올림 (1의 자리 반올림)
        insuranceAmount = Math.round(insUnitPrice * months / 1000) * 1000;
      }
    }

    // count 수만큼 행 생성
    for (let i = 1; i <= count; i++) {
      const name = count > 1 ? `${jobType} ${i}` : jobType;
      
      items.push({
        name: name,
        job: jobType,
        w4c_pay_gubun: salaryTypeName,
        expense_type: laborCostType,
        biz_gubun: salaryTypeName,
        pay: annualSalary,
        out_pay: retirementAmount,
        social_pay: insuranceAmount,
        total_pay: annualSalary + retirementAmount + insuranceAmount,
      });
    }
  });

  // 정렬: 간접비(센터장) > 직접비(사회복지사) > 직접비(요양보호사)
  items.sort((a, b) => {
    const orderMap: { [key: string]: number } = {
      '시설장(관리책임자)': 1,
      '사회복지사': 2,
      '요양보호사 1급': 3,
    };
    
    const orderA = orderMap[a.job] || 999;
    const orderB = orderMap[b.job] || 999;
    
    if (orderA !== orderB) return orderA - orderB;
    
    // 같은 직종 내에서 숫자 기반 자연 정렬 (요양보호사 1급 1, 2, 3... 10, 11...)
    const numA = parseInt(a.name.match(/\d+$/)?.[0] || '0', 10);
    const numB = parseInt(b.name.match(/\d+$/)?.[0] || '0', 10);
    return numA - numB;
  });

  return items;
});

// 사업구분 옵션
const bizGubunOptions = computed(() => {
  const uniqueBizGubuns = [...new Set(salaryData.value.map(item => item.biz_gubun))];
  return [
    { text: '전체', value: '전체' },
    ...uniqueBizGubuns.map(bizGubun => ({
      text: bizGubun,
      value: bizGubun
    }))
  ];
});

// 필터링된 데이터
const filteredSalaryData = computed(() => {
  if (selectedBizGubun.value === '전체') {
    return salaryData.value;
  }
  return salaryData.value.filter(item => item.biz_gubun === selectedBizGubun.value);
});

const headers = [
  { title: '성명', key: 'name', sortable: true },
  { title: '직종', key: 'job', sortable: true },
  { title: '급여유형', key: 'w4c_pay_gubun', sortable: true },
  { title: '비용구분', key: 'expense_type', sortable: true },
  { title: '급여', key: 'pay', sortable: true },
  { title: '퇴직금', key: 'out_pay', sortable: true },
  { title: '사회보험부담금', key: 'social_pay', sortable: true },
  { title: '총급여', key: 'total_pay', sortable: true },
];

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('ko-KR').format(num);
};

const closeModal = () => {
  dialog.value = false;
};

const downloadExcel = async () => {
  try {
    loading.value = true;
    const XLSX = await import('xlsx-js-style');

    const workbook = XLSX.utils.book_new();
    const worksheet: any = {};

    // 엑셀 다운로드 시에는 전체 데이터 사용
    const excelData = salaryData.value;

    // --- A1:J1 Message (Red text, center aligned) ---
    const message = "인건비구분/급여유형 별 선택 가능 직종 정보( * 개인정보 보호를 위하여 주민등록번호는 마스킹 처리되어 있습니다. 주민등록번호는 입력/수정 안하셔도 됩니다. )";
    worksheet['A1'] = { 
      v: message, 
      t: 's',
      s: {
        font: { color: { rgb: "FF0000" }, bold: true },
        alignment: { horizontal: "center", vertical: "center", wrapText: true }
      }
    };
    
    // Merge cells for message
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    worksheet['!merges'].push(XLSX.utils.decode_range("A1:J1"));

    // --- A2:J8 Information Table (Red text) ---
    const infoTable = [
      ["직접비", "주야간보호", "", "요양보호사1급, 요양보호사2급, 사회복지사, 간호사, 간호조무사, 물리치료사, 작업치료사"],
      ["", "방문요양, 방문목욕", "", "요양보호사1급, 요양보호사2급, 사회복지사(방문요양만 해당)"],
      ["", "방문간호", "", "요양보호사1급, 요양보호사2급, 사회복지사, 간호사, 간호조무사"],
      ["", "주야간보호", "", "간호사, 간호조무사, 치과위생사"],
      ["간접비", "주야간보호, 단기보호, 방문간호", "", "시설장(관리책임자), 사무국장, 사무원, 영양사, 조리원, 위생원, 관리인, 보조원 운전사, 기타"],
      ["", "방문요양, 방문목욕", "", "시설장(관리책임자), 사무국장, 사무원, 영양사, 조리원, 위생원, 관리인, 보조원 운전사, 사회복지사(방문목욕만 해당), 기타"],
      ["", "복지용구제공사업소", "", "시설장(관리책임자), 사무국장, 사무원, 보조원 운전사, 기타"]
    ];

    const redCenterStyle = {
      font: { color: { rgb: "FF0000" } },
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Add info table data with styling
    infoTable.forEach((row, rowIdx) => {
      row.forEach((val, colIdx) => {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx });
        worksheet[cellAddress] = { v: val, t: 's', s: redCenterStyle };
      });
      // Fill remaining columns with empty cells (J까지 확장)
      for (let c = row.length; c < 10; c++) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIdx + 1, c: c });
        worksheet[cellAddress] = { v: '', t: 's', s: redCenterStyle };
      }
    });

    // Merges for info table
    worksheet['!merges'].push(
      XLSX.utils.decode_range("A2:A5"), // 직접비
      XLSX.utils.decode_range("A6:A8"), // 간접비
      XLSX.utils.decode_range("B2:C2"),
      XLSX.utils.decode_range("B3:C3"),
      XLSX.utils.decode_range("B4:C4"),
      XLSX.utils.decode_range("B5:C5"),
      XLSX.utils.decode_range("B6:C6"),
      XLSX.utils.decode_range("B7:C7"),
      XLSX.utils.decode_range("B8:C8"),
      XLSX.utils.decode_range("D2:J2"),
      XLSX.utils.decode_range("D3:J3"),
      XLSX.utils.decode_range("D4:J4"),
      XLSX.utils.decode_range("D5:J5"),
      XLSX.utils.decode_range("D6:J6"),
      XLSX.utils.decode_range("D7:J7"),
      XLSX.utils.decode_range("D8:J8")
    );

    // --- Data Headers (엑셀 9행) with Gray Background ---
    const dataHeaders = [
      "성명", "직종", "급여유형", "인건비구분", "주민등록번호",
      "급여", "각종수당", "일용잡금", "퇴직금 및 퇴직적립금", "사회보험부담금"
    ];

    const headerStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "D3D3D3" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    dataHeaders.forEach((header, colIdx) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 8, c: colIdx });
      worksheet[cellAddress] = { v: header, t: 's', s: headerStyle };
    });

    // --- Data Rows (엑셀 10행부터) ---
    const dataStyle = {
      alignment: { horizontal: "left", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    const numberStyle = {
      alignment: { horizontal: "right", vertical: "center" },
      numFmt: '#,##0',
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    excelData.forEach((item: SalaryItem, rowIdx: number) => {
      const rowData = [
        item.name,
        item.job,
        item.w4c_pay_gubun,
        item.expense_type,
        "", // 주민등록번호 (빈 값)
        item.pay || 0,
        0, // 각종수당
        0, // 일용잡금
        item.out_pay || 0,
        item.social_pay || 0
      ];

      rowData.forEach((val, colIdx) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 9 + rowIdx, c: colIdx });
        const isNumberCol = [5, 6, 7, 8, 9].includes(colIdx);
        worksheet[cellAddress] = { 
          v: val, 
          t: isNumberCol ? 'n' : 's', 
          s: isNumberCol ? numberStyle : dataStyle 
        };
      });
    });

    // --- Set worksheet range ---
    const lastRow = excelData.length > 0 
      ? 9 + excelData.length - 1 
      : 8;
    const range = {
      s: { r: 0, c: 0 },
      e: { r: lastRow, c: 9 }
    };
    worksheet['!ref'] = XLSX.utils.encode_range(range);

    // --- Set column widths ---
    worksheet['!cols'] = [
      { wch: 18 },  // A: 성명
      { wch: 18 },  // B: 직종
      { wch: 30 },  // C: 급여유형
      { wch: 12 },  // D: 인건비구분
      { wch: 15 },  // E: 주민등록번호
      { wch: 15 },  // F: 급여
      { wch: 12 },  // G: 각종수당
      { wch: 12 },  // H: 일용잡금
      { wch: 22 },  // I: 퇴직금 및 퇴직적립금
      { wch: 18 }   // J: 사회보험부담금
    ];
    
    // Set row heights
    const totalRows = 1 + 7 + 1 + excelData.length;
    worksheet['!rows'] = Array(totalRows).fill({ hpx: 17.25 });

    XLSX.utils.book_append_sheet(workbook, worksheet, '임직원보수일람표');

    // Filename
    const sisulName = store.state.user?.m_sisul_name || '시설';
    const fileName = `${sisulName}_${props.year}년_임직원보수일람표.xlsx`;

    XLSX.writeFile(workbook, fileName);
    emit('showSnackbar', '임직원보수일람표 다운로드 완료', 'success');
  } catch (error) {
    console.error('엑셀 다운로드 중 오류:', error);
    emit('showSnackbar', '엑셀 다운로드 중 오류가 발생했습니다.', 'error');
  } finally {
    loading.value = false;
  }
};

// 외부에서 다이얼로그 열기
const open = () => {
  dialog.value = true;
};

defineExpose({
  open
});
</script>
